import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import Modal from "../Modal";

type ToastType = "success" | "error" | "info";

type Toast = {
    id: string;
    type: ToastType;
    message: string;
    timeoutMs?: number; // undefined or 0 => persistent
};

type ConfirmOptions = {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    danger?: boolean;
};

type UIMessageAPI = {
    toast: {
        success: (message: string, timeoutMs?: number) => void;
        error: (message: string, timeoutMs?: number) => void;
        info: (message: string, timeoutMs?: number) => void;

        // optional convenience for "must-close" vs auto-dismiss
        persist: {
            success: (message: string) => void;
            error: (message: string) => void;
            info: (message: string) => void;
        };
    };
    confirm: (opts: ConfirmOptions) => Promise<boolean>;
};

const UIMessageContext = createContext<UIMessageAPI | null>(null);

function uid() {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function UIMessageProvider({ children }: { children: React.ReactNode }) {
    // -------------------------
    // Toasts
    // -------------------------
    const [toasts, setToasts] = useState<Toast[]>([]);
    const timersRef = useRef<Record<string, number>>({});

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const timer = timersRef.current[id];
        if (timer) {
            window.clearTimeout(timer);
            delete timersRef.current[id];
        }
    }, []);

    const pushToast = useCallback(
        (type: ToastType, message: string, timeoutMs?: number) => {
            const id = uid();
            const toast: Toast = { id, type, message, timeoutMs };

            setToasts((prev) => [toast, ...prev].slice(0, 4)); // cap stack size

            // Only auto-dismiss if timeoutMs is a positive number
            if (typeof timeoutMs === "number" && timeoutMs > 0) {
                timersRef.current[id] = window.setTimeout(() => {
                    removeToast(id);
                }, timeoutMs);
            }
        },
        [removeToast]
    );

    // -------------------------
    // Confirm modal (promise-based)
    // -------------------------
    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        opts: ConfirmOptions | null;
        resolve: ((v: boolean) => void) | null;
    }>({ open: false, opts: null, resolve: null });

    const confirm = useCallback((opts: ConfirmOptions) => {
        return new Promise<boolean>((resolve) => {
            setConfirmState({ open: true, opts, resolve });
        });
    }, []);

    const closeConfirm = useCallback((result: boolean) => {
        setConfirmState((prev) => {
            prev.resolve?.(result);
            return { open: false, opts: null, resolve: null };
        });
    }, []);

    // Close modal on Escape
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (confirmState.open && e.key === "Escape") closeConfirm(false);
        },
        [confirmState.open, closeConfirm]
    );

    const api = useMemo<UIMessageAPI>(
        () => ({
            toast: {
                // Default: persistent until user closes (no timeout passed)
                success: (msg, ms) => pushToast("success", msg, ms),
                error: (msg, ms) => pushToast("error", msg, ms),
                info: (msg, ms) => pushToast("info", msg, ms),

                // Convenience helpers (explicit persistent)
                persist: {
                    success: (msg) => pushToast("success", msg, 0),
                    error: (msg) => pushToast("error", msg, 0),
                    info: (msg) => pushToast("info", msg, 0),
                },
            },
            confirm,
        }),
        [pushToast, confirm]
    );

    return (
        <UIMessageContext.Provider value={api}>
            <div onKeyDown={onKeyDown} tabIndex={-1}>
                {children}
            </div>

            {/* Toast host */}
            <div className="ui-toast-host" aria-live="polite" aria-relevant="additions">
                {toasts.map((t) => (
                    <div key={t.id} className={`ui-toast ui-toast--${t.type}`}>
                        <div className="ui-toast__msg">{t.message}</div>
                        <button className="ui-toast__x" onClick={() => removeToast(t.id)} aria-label="Dismiss">
                            ×
                        </button>
                    </div>
                ))}
            </div>

            {/* Confirm modal */}
            {confirmState.open && confirmState.opts && (
                <Modal
                    isOpen={confirmState.open}
                    onClose={() => closeConfirm(false)}
                    ariaLabel={confirmState.opts.title ?? "Confirm"}
                >
                    <div className="unsaved-modal">
                        {confirmState.opts.title && (
                            <div className="unsaved-modal__title">{confirmState.opts.title}</div>
                        )}
                        <div className="unsaved-modal__text">{confirmState.opts.message}</div>

                        <div className="unsaved-modal__actions">
                            <button
                                className="btn btn--white btn--size-md"
                                onClick={() => closeConfirm(false)}
                            >
                                {confirmState.opts.cancelText ?? "Cancel"}
                            </button>

                            <button
                                className={`btn btn--orange btn--size-md ${confirmState.opts.danger ? "ui-danger" : ""}`}
                                onClick={() => closeConfirm(true)}
                            >
                                {confirmState.opts.confirmText ?? "Confirm"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </UIMessageContext.Provider>
    );
}

export function useUIMessage() {
    const ctx = useContext(UIMessageContext);
    if (!ctx) throw new Error("useUIMessage must be used within UIMessageProvider");
    return ctx;
}
